import { format } from "date-fns";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useNotification } from "../../../../../hooks/useNotification";
import { useBulkUpdatePurchaseOrderStatusMutation } from "../../../../../store/apiSlice/purchaseOrderApiSlice";
import { ConsignmentTrackingControl } from "../../../../oceanTracking/TrackingPanel";
import {
  isPurchaseOrderStatusDraggable,
  isPurchaseStatusDropTarget,
} from "../../../../purchaseOrder/constants/poBoardStatusLanes";
import { NotesDrawerShell } from "../Notes/NotesDrawerShell";
import { FilesDrawerShell } from "../PurchaseOrderDetails/Files/FilesDrawerShell";
import { Status } from "../Status/Status";
import { BoardCardIcons } from "./BoardCardIcons";
import { BoardRelationshipGraphDrawer } from "./BoardRelationshipGraphDrawer";
import { PurchaseOrderBoardCard } from "./PurchaseOrderBoardCard";
import { ShipmentBoardCardTitle } from "./ShipmentBoardCardTitle";
import { ShipmentBoardFooterMeta } from "./ShipmentBoardFooterMeta";
import {
  resolveBoardCardFooterLabel,
  resolveBoardCardHeroText,
  useBoardCardHero,
} from "./boardCardDisplay";
import { BoardLaneModeIcon } from "./BoardLaneModeIcon.jsx";

/**
 * StatusBoardView Component
 *
 * Renders a Kanban-style board column for a specific status,
 * representing orders filtered by that status. Supports notes, files, and selection of tiles.
 */
export const StatusBoardView = ({
  status,
  filteredOrderByStatus,
  totalCount,
  ind,
  orderType,
  statusPalette,
  getConsignmentByRule,
  selectedCurrentView,
  handleSelectOrder,
}) => {
  const boardCardHero = useBoardCardHero(selectedCurrentView, orderType);
  const notify = useNotification();
  const [bulkUpdateStatus, { isLoading: movingStatus }] =
    useBulkUpdatePurchaseOrderStatusMutation();
  const [dragOverActive, setDragOverActive] = useState(false);
  const [isOpenNotes, setIsOpenNotes] = useState(false);
  const [isOpenFiles, setIsOpenFiles] = useState(false);
  const [orderIdForNotes, setOrderIdForNotes] = useState(null);
  const [orderIdForFiles, setOrderIdForFiles] = useState(null);
  const [graphRecord, setGraphRecord] = useState(null);

  const openNotesModal = (id, orderName, is_note_available, open_task_count = 0) => {
    setOrderIdForNotes({ id, name: orderName, is_note_available, open_task_count });
    setIsOpenNotes(true);
  };
  const closeNotesModal = () => {
    setIsOpenNotes(false);
    setOrderIdForNotes(null);
  };
  const openFilesDrawer = (id, orderName, recordDisplayName = "") => {
    setOrderIdForFiles({ id, name: orderName, recordDisplayName });
    setIsOpenFiles(true);
  };
  const closeFilesDrawer = () => {
    setIsOpenFiles(false);
    setOrderIdForFiles(null);
  };
  const openGraphDrawer = (id, orderName) => {
    setGraphRecord({ id, name: orderName });
  };
  const closeGraphDrawer = () => {
    setGraphRecord(null);
  };

  const handleStatusDragStart = (e, order) => {
    if (orderType !== "purchase" || !isPurchaseOrderStatusDraggable(order.status)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(
      "application/purchase-order-status",
      JSON.stringify({ orderId: order.id, fromStatus: order.status }),
    );
  };

  const handleStatusDragOver = e => {
    if (orderType !== "purchase") {
      return;
    }
    const raw = e.dataTransfer.types.includes("application/purchase-order-status");
    if (!raw) {
      return;
    }
    e.preventDefault();
    setDragOverActive(current => (current ? current : true));
  };

  const handleStatusDragLeave = () => {
    setDragOverActive(false);
  };

  const handleStatusDrop = useCallback(
    async e => {
      e.preventDefault();
      setDragOverActive(false);
      if (orderType !== "purchase" || movingStatus) {
        return;
      }
      let payload;
      try {
        payload = JSON.parse(e.dataTransfer.getData("application/purchase-order-status"));
      } catch (_) {
        return;
      }
      const { orderId: draggedId, fromStatus } = payload || {};
      if (!draggedId || !isPurchaseStatusDropTarget(status, fromStatus)) {
        return;
      }
      try {
        const response = await bulkUpdateStatus({
          ids: [draggedId],
          status,
        }).unwrap();
        notify({ type: "success", message: response.detail || "Status updated." });
      } catch (error) {
        const data = error?.data || {};
        notify({
          type: "error",
          message:
            data.detail ||
            [data.ids, data.status, data.non_field_errors].flat().filter(Boolean).join(" ") ||
            "Failed to update purchase order status.",
        });
      }
    },
    [bulkUpdateStatus, movingStatus, notify, orderType, status],
  );

  const laneClassName = [
    "tilesContainer",
    orderType === "purchase" && dragOverActive ? "tilesContainerDragOver" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div key={status} className="board">
      <div className="boardHeader boardHeaderWithModeIcon">
        <div className="boardHeaderLaneTitle">
          {orderType === "shipment" && (
            <BoardLaneModeIcon kind="status" statusLabel={status} />
          )}
          <Status statusName={status} palette={statusPalette} />
        </div>
        <p className="countNo">{totalCount ?? filteredOrderByStatus?.length}</p>
      </div>
      <div
        className={laneClassName}
        onClick={handleSelectOrder}
        onDragOver={handleStatusDragOver}
        onDragLeave={handleStatusDragLeave}
        onDrop={handleStatusDrop}
      >
        <NotesDrawerShell
          isOpen={isOpenNotes && Boolean(orderIdForNotes)}
          onClose={closeNotesModal}
          recordName={orderIdForNotes?.name}
          openTaskCount={orderIdForNotes?.open_task_count}
          key={orderIdForNotes?.id}
          orderId={orderIdForNotes?.id}
          isNoteAvailable={orderIdForNotes?.is_note_available}
          orderType={orderType}
          setOrderIdForNotes={setOrderIdForNotes}
          getConsignmentByRule={getConsignmentByRule}
          selectedCurrentView={selectedCurrentView}
        />
        <FilesDrawerShell
          isOpen={isOpenFiles && Boolean(orderIdForFiles)}
          onClose={closeFilesDrawer}
          orderId={orderIdForFiles?.id}
          orderName={orderIdForFiles?.name}
          recordDisplayName={orderIdForFiles?.recordDisplayName}
          orderType={orderType}
          getConsignmentByRule={getConsignmentByRule}
        />
        <BoardRelationshipGraphDrawer
          orderType={orderType}
          isOpen={Boolean(graphRecord)}
          onClose={closeGraphDrawer}
          record={graphRecord}
          onOpenFiles={openFilesDrawer}
          onOpenNotes={openNotesModal}
        />
        {filteredOrderByStatus &&
          filteredOrderByStatus.map(order =>
            orderType === "shipment" ? (
              <div key={order.id} className="boardTile shipmentBoardTile">
                <div className="tileHeader">
                  <div className="left">
                    <input className="checkBox" type="checkbox" value={JSON.stringify(order)} />
                    <ShipmentBoardCardTitle
                      orderId={order.id}
                      name={resolveBoardCardHeroText(order, orderType, boardCardHero)}
                    />
                  </div>
                  <BoardCardIcons
                    order={order}
                    onOpenFiles={openFilesDrawer}
                    onOpenNotes={openNotesModal}
                    onOpenGraph={openGraphDrawer}
                  />
                </div>
                <div className="facilityAndAsn">
                  <div className="destinationFacility">
                    <span>Destination facility</span>
                    <strong>{order.to_warehouse?.name || order.to_warehouse || "—"}</strong>
                  </div>
                  <div className="shipmentDetails">
                    <div>
                      <span>ASN</span>
                      <strong>{order.asn?.name || "Pending"}</strong>
                    </div>
                  </div>
                </div>
                <div className="tileFooter">
                  <ShipmentBoardFooterMeta
                    orderId={order.id}
                    reference={resolveBoardCardFooterLabel(order, orderType, boardCardHero)}
                    etaDisplay={
                      order.eta || order.estimated_date
                        ? format(order.eta || order.estimated_date, "dd MMM yyyy, hh:mm a")
                        : "Pending"
                    }
                  />
                  <div className="trackerActions">
                    <ConsignmentTrackingControl
                      consignment={order.id}
                      shipmentNumber={order.name}
                      onTrackingUpdated={getConsignmentByRule}
                      deferQuery
                      initialHasTracking={order.has_ocean_tracking}
                      renderTrigger={({ disabled, hasTracking, label, onClick }) => (
                        <button
                          className="trackDetails"
                          type="button"
                          disabled={disabled}
                          title={label}
                          onClick={onClick}
                        >
                          {hasTracking ? "Show Tracker" : "Track"} <span aria-hidden="true">→</span>
                        </button>
                      )}
                    />
                    <div className="statusAndPriority">
                      <Status statusName={order.status} palette={statusPalette} />
                    </div>
                  </div>
                </div>
              </div>
            ) : orderType === "purchase" ? (
              <PurchaseOrderBoardCard
                key={order.id}
                order={order}
                orderType={orderType}
                boardCardHero={boardCardHero}
                draggable={isPurchaseOrderStatusDraggable(order.status) && !movingStatus}
                onDragStart={e => handleStatusDragStart(e, order)}
                onOpenFiles={openFilesDrawer}
                onOpenNotes={openNotesModal}
                onOpenGraph={openGraphDrawer}
              />
            ) : (
              <div key={order.id} className="boardTile">
                <div className="tileHeader">
                  <div className="left">
                    <input className="checkBox" type="checkbox" value={JSON.stringify(order)} />
                    <Link to={`${order.id}`}>{order.name} </Link>
                  </div>
                  <BoardCardIcons
                    order={order}
                    onOpenFiles={openFilesDrawer}
                    onOpenNotes={openNotesModal}
                  />
                </div>
                <div className="orderInfo">
                  <p>To Facility: {order.to_warehouse}</p>
                  <p>Company: {order.tracking ? order.tracking?.logistics_partner : ""}</p>
                  <p>ASN number: {order.asn ? order.asn.name : ""} </p>
                </div>
                <p className="eta">
                  ETA:{" "}
                  {order.eta || order.estimated_date
                    ? format(order.eta || order.estimated_date, "dd MMM yyyy, hh:mm a")
                    : "No eta "}
                </p>
              </div>
            ),
          )}
      </div>
    </div>
  );
};
